import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

type DeleteInvoiceReceiverVariables = {
  companyId: UUID;
  email: string;
};

/**
 * Deletes an invoice receiver from a company.
 *
 * @param variables - The variables needed for the mutation.
 * @param variables.companyId - The ID of the company.
 * @param variables.email - The email address to delete.
 * @returns A promise that resolves when the invoice receiver is deleted.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/delete_company_invoice_receivers_company__company_id__invoice_receivers__email__delete
 */
// fallow-ignore-next-line unused-export
export const deleteCompanyInvoiceReceiver = async ({
  companyId,
  email,
}: DeleteInvoiceReceiverVariables) => {
  try {
    return await vestigasApi
      .delete(ENDPOINT.COMPANY.INVOICE_RECEIVERS.DELETE(companyId, email), {
        validate: { response: zodSuccessResponseSchema },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error deleting invoice receiver',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for deleting an invoice receiver from a company.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 */
export const useMutationCompanyDeleteInvoiceReceiver = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, DeleteInvoiceReceiverVariables>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, DeleteInvoiceReceiverVariables>({
    mutationFn: deleteCompanyInvoiceReceiver,
    onSettled(_data, _error, variables) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceReceivers(variables.companyId),
      });
    },
    ...options,
  });
};
